import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { getMonthToDateRange } from '@/utils/date';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { ZonalStatementListSearch } from '../types/zonalStatement.types';
import {
  toZonalStatementFilterSearch,
  toZonalStatementFilterValues,
} from '../utils/zonalStatementFilters';

const route = getRouteApi('/_auth/dashboards/zonal-statement/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toZonalStatementFilterValues(toZonalStatementFilterSearch(values));

export function useZonalStatementFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toZonalStatementFilterValues(search), [search]);
  const defaultValues = useMemo(() => getMonthToDateRange(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toZonalStatementFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const params = useMemo(
    (): ZonalStatementListSearch & { dateFrom: string; dateTo: string } => ({
      ...search,
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search, defaultValues],
  );

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, params };
}
